import React, { useEffect, useRef, useState } from "react";
import { getImageUrl } from "../../../../commons/assets_path";
import style from "./MemberManagement.module.scss";
import Modal from "react-modal";
import axios from "axios";
import { PageLoader } from "../../Loaders/PageLoader";
import { Loader } from "../../Loaders/Loader";
import { useTranslation } from "react-i18next";

type Props = {
  client: string;
  changeModalCss: (width: string, height: string) => void;
  setIsMenuOpen: (isOpen: boolean) => void;
  setHeaderShow: (show: boolean) => void;
};

type Mode = "default" | "edit";

type Member = {
  id: number;
  name: string;
  role_name: string;
  email: string;
  invited_database: string;
};

const customStyles: Modal.Styles = {
  overlay: {
    zIndex: 1501,
    background: "rgba(34,34,34, 0.3)",
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: 765,
    width: "100%",
    borderRadius: 12,
    padding: 0,
  },
};

const GuestManagement = ({ client, changeModalCss, setIsMenuOpen, setHeaderShow }: Props) => {
  const { t } = useTranslation();

  const [isDeleteModal, setIsDeleteModal] = useState<boolean>(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("admin");
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [targetMember, setTargetMember] = useState<Member | null>(null);
  const [limit, setLimit] = useState<number>(10);
  const [message, setMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [offsetCount, setOffsetCount] = useState<number>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<Mode>("default");

  const handleRoleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRole(event.target.value);
  };

  const toggleDropdown = (memberId: number) => {
    setOpenDropdownId(openDropdownId === memberId ? null : memberId);
  };

  const getGuests = async (isSearch: boolean = false, query: string | null = null, offset: number = offsetCount) => {
    const response = await axios.get(`/${client}/settings/client_members/members`, {
      params: { limit: limit, offset: offset, is_guest: true, query: query },
    });
    if (isSearch) {
      setMembers(response.data);
    } else {
      setMembers((prevMembers) => [...prevMembers, ...response.data]);
      setOffsetCount(offset + 10);
    }
    setLoading(false);
  };

  const updateRole = async (guestId: number, role: string) => {
    setBtnLoading(true);
    const response = await axios.patch(`/${client}/settings/client_members/${guestId}/update_to_member`, {
      client_member: {
        role: role,
      },
    });
    if (response.status === 200) {
      setMessage(response.data.message);
      getGuests(true);
      setMode("default");
      setIsMenuOpen(true);
      changeModalCss("100%", "auto");
      setHeaderShow(true);
    }
    setBtnLoading(false);
  };

  const deleteGuest = async (guestId: number) => {
    setBtnLoading(true);
    const response = await axios.delete(`/${client}/settings/client_members/${guestId}/delete_guest`);
    if (response.status === 200) {
      setMessage(response.data.message);
      getGuests(true, null, 0);
      setIsDeleteModal(false);
    }
    setBtnLoading(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    getGuests(false);
  }, [limit]);

  useEffect(() => {
    if (message !== "") {
      setTimeout(() => {
        setMessage("");
      }, 7000);
    }
  }, [message]);

  const closeModal = () => {
    setMode("default");
    setIsMenuOpen(true);
    setHeaderShow(true);
    changeModalCss("100%", "auto");
  };

  const openModal = (newMode: Mode) => {
    setMode(newMode);
    setIsMenuOpen(false);
    setHeaderShow(false);
    changeModalCss("765px", "auto");
  };

  const renderContent = () => {
    switch (mode) {
      case "edit":
        return (
          <div className={style.actionModal}>
            <div className={style.modalTitle} data-testid="test-edit-member-modal">
              {t("GuestManagement.メンバーに変更する")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div className={style.modalText}>{t("GuestManagement.変更するゲストの名前")}</div>
              <div>{targetMember?.name}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div className={style.modalText}>{t("GuestManagement.変更するゲストのメールアドレス")}</div>
              <div>{targetMember?.email}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div className={style.modalText}>{t("GuestManagement.変更するゲストの権限")}</div>
              <div className="select-wrap">
                <select className="form-control pulldownSelect" value={selectedRole} onChange={handleRoleChange}>
                  <option value="admin">{t("GuestManagement.管理者")}</option>
                  <option value="member">{t("GuestManagement.メンバー")}</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "row", gap: "9px", justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={closeModal}>
                {t("GuestManagement.キャンセル")}
              </button>
              <button
                className="btn btn-info d-flex align-items-center justify-content-center"
                onClick={() => targetMember && updateRole(targetMember.id, selectedRole)}
              >
                {btnLoading ? <Loader /> : t("GuestManagement.更新")}
              </button>
            </div>
          </div>
        );
      default:
        return (
          <>
            {loading ? (
              <PageLoader />
            ) : (
              <div className={style.container}>
                {message && (
                  <div className="flash_message notice" style={{ zIndex: 9999 }}>
                    {message}
                  </div>
                )}
                <div className={style.header}>
                  <div className={style.headerInfo}>
                    <div className={style.title} data-testid="guest-management-settings-title">
                      {t("GuestManagement.ゲスト管理")}
                    </div>
                    <div className={style.description}>
                      {t("GuestManagement.現在招待されているゲストの削除・メンバーへの変更が可能です。")}
                      <p>{t("GuestManagement.ゲストの招待は各データベース右上の「共有」より実施してください。")}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className={style.search}>
                    <img
                      src={getImageUrl("images/icons/searchGray.svg")}
                      style={{ width: "20px", height: "20px" }}
                      className="ml-1"
                    />
                    <input
                      type="text"
                      placeholder={t("GuestManagement.ゲストを検索")}
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        getGuests(true, e.target.value, 0);
                      }}
                      data-testid="test-search-input"
                    />
                  </div>
                </div>
                <div>
                  <div className={style.memberListHeader}>
                    <div className={style.memberListHeaderItem} style={{ width: "260px" }}>
                      {t("GuestManagement.名前")}
                    </div>
                    <div className={style.memberListHeaderItem} style={{ width: "210px" }}>
                      {t("GuestManagement.権限")}
                    </div>
                    <div className={style.memberListHeaderItem}>{t("GuestManagement.参加データベース")}</div>
                  </div>
                  <div className={style.memberListBody}>
                    {members.map((member, index) => (
                      <div key={member.id} className={style.memberItem}>
                        <div className={style.memberItemInfo} style={{ width: "260px" }}>
                          <div
                            style={{
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {member.name}
                          </div>
                          <div
                            style={{
                              color: "#777777",
                              fontSize: "12px",
                              width: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {member.email}
                          </div>
                        </div>
                        <div className={style.memberItemInfo} style={{ width: "210px" }}>
                          {member.role_name}
                        </div>
                        <div className={style.memberItemInfo}>{member.invited_database}</div>
                        <div className={style.dropdownContainer}>
                          <img
                            src={getImageUrl("images/icons/option.svg")}
                            alt={t("GuestManagement.オプション")}
                            style={{ width: "20px", height: "20px", position: "relative", cursor: "pointer" }}
                            onClick={() => toggleDropdown(member.id)}
                            data-testid="test-open-drop-menu"
                          />
                          {openDropdownId === member.id && (
                            <div
                              ref={dropdownRef}
                              className={style.dropdownMenu}
                              style={{
                                top: members.length === 1 ? "-20px" : index === members.length - 1 ? "-50px" : "100%",
                              }}
                            >
                              <div
                                className={style.dropdownItem}
                                onClick={() => {
                                  setTargetMember(member);
                                  openModal("edit");
                                }}
                                data-testid="test-change-to-member"
                              >
                                {t("GuestManagement.メンバーに変更")}
                              </div>
                              <div
                                className={style.dropdownItem}
                                style={{ color: "#ff4958" }}
                                onClick={() => {
                                  setIsDeleteModal(true);
                                  setTargetMember(member);
                                }}
                                data-testid="test-delete-to-member"
                              >
                                {t("GuestManagement.削除")}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {members.length > 0 && members.length % 10 === 0 && (
                      <div
                        style={{
                          color: "#0a6982",
                          cursor: "pointer",
                          textAlign: "center",
                          marginTop: "30px",
                          marginBottom: "30px",
                        }}
                        onClick={() => setLimit(limit + 10)}
                        data-testid="test-more-fetch"
                      >
                        {t("GuestManagement.さらに読み込む")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        );
    }
  };

  return (
    <>
      {mode === "edit" ? (
        renderContent()
      ) : (
        <>
          {renderContent()}
          <Modal isOpen={isDeleteModal} onRequestClose={() => setIsDeleteModal(false)} style={customStyles}>
            <div className={style.modal} data-testid="test-delete-member-modal">
              <div>
                <div className={style.modalTitle}>{t("GuestManagement.本当に削除しますか？")}</div>
                <div className={style.modalDescription}>
                  {/* {`${targetMember?.name}さんが作成したフローボットは${myAccount.email}のアカウントに紐づきます。本当に削除しますか?`} */}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "row", gap: "9px", justifyContent: "flex-end" }}>
                <button className="btn btn-secondary" onClick={() => setIsDeleteModal(false)}>
                  {t("GuestManagement.キャンセル")}
                </button>
                <button
                  className="btn btn-info d-flex align-items-center justify-content-center"
                  onClick={() => targetMember && deleteGuest(targetMember.id)}
                >
                  {btnLoading ? <Loader /> : t("GuestManagement.はい")}
                </button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default GuestManagement;
